import { type ReactNode, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Campaigns } from '@/components/pages/campaigns';
import { AllOpenings } from '@/components/pages/all-openings';
import { Channels } from '@/components/pages/channels';
import { Dashboard } from '@/components/pages/dashboard';
import { EditCampaign } from '@/components/pages/edit-campaign';
import { Funnel } from '@/components/pages/funnel';
import { JobPosting, JobPostingBatch } from '@/components/pages/job-posting';
import { Layout } from '@/components/pages/layout';
import { OpeningDetails } from '@/components/pages/opening-details';
import { Openings } from '@/components/pages/openings';
import { Settings } from '@/components/pages/settings';
import { AutomationSettings } from '@/components/pages/settings/automation';
import { ChannelSettings } from '@/components/pages/settings/channels';
import { Contracts } from '@/components/pages/settings/contracts';
import { DashboardSettings } from '@/components/pages/settings/dashboard';
import { NotFound } from '@/components/ui/not-found';
import { OpeningCueContext, OpeningProvider } from '@/contexts/opening-context';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useSourceFreemium } from '@/hooks/use-source-freemium';

import { RouteAuth } from './route-auth';

/**
 * Where an account lands when it opens Source without a path. A free-tier
 * account goes to `/campaigns`, since the dashboard it would otherwise land on
 * is locked.
 */
function useLandingRoute() {
  return useSourceFreemium() ? 'campaigns' : 'dashboard';
}

function LandingRedirect() {
  const landingRoute = useLandingRoute();

  return <Navigate to={landingRoute} replace />;
}

/**
 * Free-tier home. A subscribed account that lands here is sent to the
 * dashboard — `/openings` is not a subscribed nav destination.
 */
function OpeningsRoute() {
  const isFreemium = useSourceFreemium();
  const { dashboardPath } = useNavigationPaths();
  const { search } = useLocation();

  if (!isFreemium)
    return <Navigate to={{ pathname: dashboardPath, search }} replace />;

  return <Openings />;
}

/**
 * Subscribed-only openings list. Its rows carry the mutation that adds an
 * opening to sourcing, so a free-tier account is turned away at the route
 * rather than served a page whose controls it cannot use.
 */
function AllOpeningsRoute() {
  const isFreemium = useSourceFreemium();
  const { allCampaignsPath } = useNavigationPaths();
  const { search } = useLocation();

  if (isFreemium)
    return <Navigate to={{ pathname: allCampaignsPath, search }} replace />;

  return <AllOpenings />;
}

/**
 * Keeps one canonical URL for the dashboard tree per account tier: a free-tier
 * account reaches Source as `/openings`, so links that still point at the
 * dashboard, an opening's details, or their channel/funnel/edit children —
 * breadcrumbs, bookmarks, the back-navigation fallback — follow it there. The
 * query string carries over so a deep link keeps its filter state.
 */
function SubscribedDashboardGuard({ children }: { children: ReactNode }) {
  const isFreemium = useSourceFreemium();
  const { openingsPath } = useNavigationPaths();
  const { search } = useLocation();

  if (isFreemium)
    return <Navigate to={{ pathname: openingsPath, search }} replace />;

  return <>{children}</>;
}

function DashboardRoute() {
  return (
    <SubscribedDashboardGuard>
      <Dashboard />
    </SubscribedDashboardGuard>
  );
}

/**
 * Subscribed-only metrics-period settings. The tab is hidden on the free
 * tier, so a bookmark or typed URL is sent to Automation instead.
 */
function DashboardSettingsRoute() {
  const isFreemium = useSourceFreemium();
  const { sourceSettingsPath } = useNavigationPaths();
  const { search } = useLocation();

  if (isFreemium)
    return (
      <Navigate
        to={{ pathname: `${sourceSettingsPath}/automation`, search }}
        replace
      />
    );

  return <DashboardSettings />;
}

function OpeningDetailsRoute() {
  return (
    <SubscribedDashboardGuard>
      <OpeningDetails />
    </SubscribedDashboardGuard>
  );
}

function getSharedRoutes() {
  return (
    <>
      <Route element={<Layout />}>
        <Route index element={<LandingRedirect />} />

        {/* Free-tier home: locked dashboard preview */}
        <Route path="openings" element={<OpeningsRoute />} />

        {/* Subscribed openings list (manage sourcing inclusion) */}
        <Route path="all-openings" element={<AllOpeningsRoute />} />

        {/* Dashboard */}
        <Route path="dashboard" element={<DashboardRoute />} />

        {/* All Campaigns */}
        <Route path="campaigns" element={<Campaigns />}>
          <Route
            path=":channelSlug/:sourcingPurchaseId/edit"
            element={<EditCampaign />}
          />
        </Route>

        {/* Settings */}
        <Route path="settings" element={<Settings />}>
          <Route index element={<Navigate to="automation" replace />} />
          <Route path="automation" element={<AutomationSettings />} />
          <Route path="channels" element={<ChannelSettings />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="dashboard" element={<DashboardSettingsRoute />} />
        </Route>

        {/* Opening Details — same freemium guard as /dashboard */}
        <Route path="dashboard/:openingSlug" element={<OpeningDetailsRoute />}>
          <Route index element={<RedirectToChannels />} />
          <Route path="channels" element={<Channels />}>
            <Route
              path=":channelSlug/:sourcingPurchaseId/edit"
              element={<EditCampaign />}
            />
          </Route>
          <Route path="funnel" element={<Funnel />} />
        </Route>
      </Route>

      <Route path="post-job" element={<JobPosting />} />
      {/* A posting batch's live page; it never reads the async-posting flag. */}
      <Route path="post-job/:bulkPostId" element={<JobPostingBatch />} />

      {/* Reach uses a full-page editor (publisher selector + metrics), not
          the nested dialog outlet. `reach` is a path literal so
          `channelSlug` is unset and EditCampaign renders ReachCampaignPage.
          OpeningProvider is required because this route sits outside
          OpeningDetails. Channels and All Campaigns both navigate here. */}
      <Route
        path="dashboard/:openingSlug/channels/reach/:sourcingPurchaseId/edit"
        element={
          <SubscribedDashboardGuard>
            <ReachEditCampaign />
          </SubscribedDashboardGuard>
        }
      />

      {/* Fallback route */}
      <Route path="*" Component={NotFound} />
    </>
  );
}

/**
 * Index redirect for `/dashboard/:openingSlug` → `…/channels`. Preserves the
 * current `?hiring_goal_id=…` (and any other query string) so a deep link
 * like `/dashboard/foo?hiring_goal_id=abc` lands on channels with the goal
 * still pinned.
 */
function RedirectToChannels() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: 'channels', search }} replace />;
}

function ReachEditCampaign() {
  return (
    <Suspense fallback={null}>
      <OpeningProvider>
        <OpeningCueContext />
        <EditCampaign />
      </OpeningProvider>
    </Suspense>
  );
}

export function AppRoutes() {
  const accountSlug = useAccountSlug();
  const landingRoute = useLandingRoute();

  return (
    <Routes>
      {/* Send the root path ("/") to the account's landing page */}
      <Route
        index
        element={<Navigate to={`/${accountSlug}/${landingRoute}`} replace />}
      />

      {/* Routes with commit SHA and account slug - more specific route first */}
      <Route path=":commitHash/:accountSlug/*" element={<RouteAuth />}>
        {getSharedRoutes()}
      </Route>

      {/* Routes with account slug only */}
      <Route path=":accountSlug/*" element={<RouteAuth />}>
        {getSharedRoutes()}
      </Route>
    </Routes>
  );
}
